<template>
  <div class="agent-configs-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Agent Configs
        </h1>
        <v-btn
          color="primary"
          class="mank-btn"
          elevation="0"
          :to="{ name: 'CreateAgentConfig' }">
          <Icon name="plus" variant="solid" color="onPrimary"/>
          Create Config
        </v-btn>
      </div>

      <div class="main-content">
        <div v-if="loading" class="py-12 text-center">
          <v-progress-circular indeterminate color="primary" size="40"/>
        </div>

        <div v-else-if="configs.length === 0" class="empty-state text-center py-12">
          <v-icon size="64" color="grey-lighten-1">
            mdi-cog
          </v-icon>
          <h3 class="ds-panel-title mt-4 mb-2">
            No Agent Config Created
          </h3>
          <p class="text-body-3 text--secondary mb-6">
            Create your first config and manage it from this page.
          </p>
        </div>

        <div v-else class="table-area table-area--pad">
          <v-data-table
            :headers="headers"
            :items="configs"
            :loading="loading"
            item-key="id"
            class="agent-configs-table"
            no-data-text="No config found">
            <template #item.description="{ item }">
              <div class="description-preview">
                {{ previewText(item.description) }}
              </div>
            </template>
            <template #item.external_id="{ item }">
              <span class="text-element-2">{{ item.external_id || '-' }}</span>
            </template>

            <template #item.updated_at="{ item }">
              <span class="text-element-2">{{ formatDate(item.updated_at) }}</span>
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex align-center">
                <v-btn
                  icon
                  small
                  class="mank-btn-icon mr-2"
                  :to="{ name: 'AgentConfigDetail', params: { externalId: item.external_id } }">
                  <v-icon size="16">
                    mdi-eye-outline
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  class="mank-btn-icon mr-2"
                  :to="{ name: 'EditAgentConfig', params: { externalId: item.external_id } }">
                  <Icon name="edit" variant="solid" size="16" color="default"/>
                </v-btn>
                <v-btn icon small class="mank-btn-icon" @click="openDelete(item)">
                  <Icon name="trash" variant="solid" size="16" color="default"/>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import AgentConfigService from '@/services/AgentConfigService';

export default {
  name: 'AgentConfigs',
  components: {
    Icon,
    DeleteConfirmationModal,
  },
  data() {
    return {
      loading: false,
      configs: [],
      showDeleteDialog: false,
      deleteTarget: null,
      headers: [
        { text: 'Description', value: 'description', sortable: false, width: '24%' },
        { text: 'External ID', value: 'external_id', sortable: false },
        { text: 'Updated At', value: 'updated_at', sortable: false, width: '180px' },
        { text: '', value: 'actions', sortable: false, align: 'end', width: '140px' },
      ],
    };
  },
  mounted() {
    this.fetchConfigs();
  },
  methods: {
    previewText(value) {
      if (!value) return '-';
      const compact = value.replace(/\s+/g, ' ').trim();
      return compact.length > 95 ? `${compact.slice(0, 95)}...` : compact;
    },
    formatDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString('tr-TR');
    },
    async fetchConfigs() {
      this.loading = true;
      try {
        const res = await AgentConfigService.getAllAgentConfigs();
        if (res?.success && Array.isArray(res.data)) {
          this.configs = res.data;
        } else {
          this.configs = [];
        }
      } catch (e) {
        this.configs = [];
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Agent configs could not be loaded.',
        }, { root: true });
      } finally {
        this.loading = false;
      }
    },
    openDelete(item) {
      this.deleteTarget = item;
      this.showDeleteDialog = true;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.deleteTarget = null;
    },
    async confirmDelete() {
      if (!this.deleteTarget?.external_id) return;
      try {
        await AgentConfigService.deleteAgentConfigByExternalId(this.deleteTarget.external_id);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: 'Agent config deleted.',
        }, { root: true });
        await this.fetchConfigs();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Delete failed.',
        }, { root: true });
      } finally {
        this.cancelDelete();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/colors.scss';

.main-content {
  padding: 24px;
  overflow-y: auto;
}

.agent-configs-table {
  background: $light-surface-2;
}

.description-preview {
  max-width: 420px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
